// TableWithDateRangeAndSearchPagination

import React, { FC, useEffect } from 'react';
import PaginationWithDateRangeAndSearch, {
  PaginationWithDateRangeAndSearchProps,
} from 'components/Paginations/PaginationWithDateRangeAndSearch';
import PaginationContainer from 'components/Paginations/PaginationContainer';
import { DEFAULT_PAGINATION_VALUE, PaginationLabels, Value as PaginationValue } from 'components/Paginations';
import { DEFAULT_DATE_RANGE_PICKER_VALUE, DateRange } from 'components/DateRangePicker';
import { mergeTwoObjects } from 'utils';
import BasicTable, { BasicTableProps, FilterOptions, RequestFilters, SelectedFind, SortValue } from './BasicTable';
import TableWrapper, { TableWrapperProps } from './TableWrapper';

export type TableWithDateRangeAndSearchPaginationChanges = RequestFilters & {
  pagination?: PaginationValue;
  dates?: DateRange;
  search?: string;
  accountType?: string;
};

export type TableWithDateRangeAndSearchPaginationProps = Pick<
  BasicTableProps,
  'cellRenderer' | 'data' | 'columns' | 'tableLayout' | 'stickyHeader' | 'stickyHeaderOffsets' | 'hasNoFilters'
> & {
  tableWrapperProps?: TableWrapperProps;
  paginationLabels?: PaginationLabels;
  filtersData?: {
    find?: FilterOptions;
    count: number;
  };
  filters?: {
    order?: SortValue;
    find?: SelectedFind;
    dates?: DateRange;
    pagination?: PaginationValue;
    accountType?: string;
  };
  defaultFilters?: {
    order?: SortValue;
    find?: SelectedFind;
    dates?: DateRange;
    pagination?: PaginationValue;
    accountType?: string;
  };
  shouldApplyChangesOnMount?: boolean;
  isNotEndDateLimited?: boolean;
  applyChanges: (value: TableWithDateRangeAndSearchPaginationChanges) => void;
  hasFixedPagination?: boolean;
  dateFormat?: string;
  searchBarProps?: Partial<Omit<Pick<PaginationWithDateRangeAndSearchProps, 'searchBarProps'>, 'onChange'>>;
  isHideAccountType?: boolean;
};

const TableWithDateRangeAndSearchPagination: FC<TableWithDateRangeAndSearchPaginationProps> = ({
  data,
  columns,
  cellRenderer,
  tableWrapperProps,
  paginationLabels,
  filtersData,
  filters,
  defaultFilters,
  shouldApplyChangesOnMount,
  applyChanges,
  isNotEndDateLimited,
  hasFixedPagination = true,
  dateFormat = 'DMY',
  searchBarProps,
  tableLayout,
  stickyHeader,
  stickyHeaderOffsets,
  hasNoFilters,
  isHideAccountType,
}) => {
  const dateRangePickerProps = mergeTwoObjects(
    {
      value: filters?.dates,
      defaultValue: defaultFilters?.dates || DEFAULT_DATE_RANGE_PICKER_VALUE,
      isNotEndDateLimited,
      onChange: (value: DateRange) => applyChanges({ dates: value }),
      dateFormat,
    },
    {},
  );

  const searchBarPropsModified = mergeTwoObjects(
    {
      onChange: (value: string) => applyChanges({ search: value }),
    },
    searchBarProps || {},
  );

  const accountTypeSelectProps = mergeTwoObjects(
    {
      value: filters?.accountType,
      onChange: (value: string) => applyChanges({ accountType: value }),
      isHideAccountType,
    },
    {},
  );

  const paginationProps = mergeTwoObjects(
    {
      accountTypeSelectProps,
      dateRangePickerProps,
      searchBarProps: searchBarPropsModified,
      count: filtersData?.count,
      value: filters?.pagination,
      defaultValue: defaultFilters?.pagination || DEFAULT_PAGINATION_VALUE,
      paginationLabels,
      onChange: (value: PaginationValue) => applyChanges({ pagination: value }),
    },
    {},
  ) as PaginationWithDateRangeAndSearchProps;

  const tableProps = mergeTwoObjects(
    {
      data,
      columns,
      cellRenderer,
      filtersData,
      filters,
      defaultFilters,
      applyChanges: (value: RequestFilters) => applyChanges(value),
      tableLayout,
      stickyHeader,
      stickyHeaderOffsets,
      hasNoFilters,
    },
    {},
  ) as BasicTableProps;

  useEffect(() => {
    const shouldApply = typeof shouldApplyChangesOnMount === 'boolean' ? shouldApplyChangesOnMount : true;
    if (shouldApply) {
      applyChanges(
        mergeTwoObjects(
          filters || {},
          defaultFilters || {
            dates: dateRangePickerProps.defaultValue,
            pagination: paginationProps.defaultValue,
            ...(defaultFilters || {}),
          },
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TableWrapper {...(tableWrapperProps || {})}>
      <PaginationContainer top={0} isFixed={hasFixedPagination}>
        <PaginationWithDateRangeAndSearch {...paginationProps} />
      </PaginationContainer>
      <BasicTable {...tableProps} stickyHeader={hasFixedPagination || stickyHeader} shouldApplyChangesOnMount={false} />
    </TableWrapper>
  );
};

export default TableWithDateRangeAndSearchPagination;
